import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import { useMutation, useQuery } from '@apollo/client';
import COPY_ORDER_MODEL_VARIANTS_FROM_MODEL from 'api/mutations/COPY_ORDER_MODEL_VARIANTS_FROM_MODEL';
import SET_ORDER_MODEL_TO_NOT_READY from 'api/mutations/SET_ORDER_MODEL_TO_NOT_READY';
import SET_ORDER_MODEL_TO_READY from 'api/mutations/SET_ORDER_MODEL_TO_READY';
import UPDATE_ORDER_MODEL_VARIANT from 'api/mutations/UPDATE_ORDER_MODEL_VARIANT';
import GET_MODEL_SPECIFICATION_VARIANTS from 'api/queries/GET_MODEL_SPECIFICATION_VARIANTS';
import GET_ORDER from 'api/queries/GET_ORDER';
import GET_ORDER_SUPPLIERS from 'api/queries/GET_ORDER_SUPPLIERS';
import GET_ORDER_MODEL_VARIANTS from 'api/queries/GET_ORDER_MODEL_VARIANTS';
import { confirmationDialog } from 'components/common/ConfirmationDialog/ConfirmationDialog';
import SmartTable from 'components/common/SmartTable';
import ThemedButton from 'components/common/ThemedButton';
import ThemedSwitch from 'components/common/ThemedSwitch';
import UpIcon from 'images/icons/icn-arrow.svg';
import parseOrderModelVariants from 'parsers/specificationProductionDetails/parseOrderModelVariants';
import parseSpecificationVariants from 'parsers/specificationProductionDetails/parseSpecificationVariants';
import Matrix from 'utils/Matrix';
import useDebounceMatrix from 'components/common/hooks/useDebounceMatrix';
import QuantityCell from '../QuantityCell';
import { dataValidationErrorHandler } from '../../../errors/dataValidationError';
const ModelVolume = ({ order, orderModel, }) => {
    const { t } = useTranslation('orders');
    const [rowBusy, setRowBusy] = useState([]);
    const { data: modelVolumeData } = useQuery(GET_MODEL_SPECIFICATION_VARIANTS, {
        variables: { modelSpecificationId: orderModel.modelSpecificationNode.id },
    });
    const { data: orderVolumeData } = useQuery(GET_ORDER_MODEL_VARIANTS, {
        variables: { id: orderModel.id },
    });
    const [updateOrderModelVariant] = useMutation(UPDATE_ORDER_MODEL_VARIANT, {
        refetchQueries: [
            { query: GET_ORDER_MODEL_VARIANTS, variables: { id: orderModel.id } },
            'GetOrders',
            { query: GET_ORDER_SUPPLIERS, variables: { id: order.id } },
        ],
    });
    const [copyOrderModelVariantsFromModel] = useMutation(COPY_ORDER_MODEL_VARIANTS_FROM_MODEL, {
        refetchQueries: [
            { query: GET_ORDER_MODEL_VARIANTS, variables: { id: orderModel.id } },
            'GetOrders',
            { query: GET_ORDER_SUPPLIERS, variables: { id: order.id } },
        ],
    });
    const statusMutationOptions = {
        refetchQueries: [
            { query: GET_ORDER_MODEL_VARIANTS, variables: { id: orderModel.id } },
            {
                query: GET_MODEL_SPECIFICATION_VARIANTS,
                variables: { modelSpecificationId: orderModel.modelSpecificationNode.id },
            },
            { query: GET_ORDER_SUPPLIERS, variables: { id: order.id } },
            'GetOrders',
            { query: GET_ORDER, variables: { id: order.id } },
        ],
    };
    const [setOrderModelToReady] = useMutation(SET_ORDER_MODEL_TO_READY, statusMutationOptions);
    const [setOrderModelToNotReady] = useMutation(SET_ORDER_MODEL_TO_NOT_READY, statusMutationOptions);
    const modelVariants = useMemo(() => parseSpecificationVariants(modelVolumeData === null || modelVolumeData === void 0 ? void 0 : modelVolumeData.node), [modelVolumeData]);
    const modelMatrix = useMemo(() => Matrix.create({
        w: modelVariants.variants.length,
        object: modelVariants.variants,
        mapRow: (row) => row === null || row === void 0 ? void 0 : row.quantities,
        mapCell: (variant) => ({
            id: variant === null || variant === void 0 ? void 0 : variant.id,
            value: variant === null || variant === void 0 ? void 0 : variant.quantity,
        }),
        h: undefined,
        fill: undefined,
        data: undefined,
        rows: undefined,
    }), [modelVariants]);
    const orderVariants = useMemo(() => parseOrderModelVariants(orderVolumeData === null || orderVolumeData === void 0 ? void 0 : orderVolumeData.node), [orderVolumeData]);
    const orderMatrix = useMemo(() => Matrix.create({
        w: orderVariants.variants.length,
        object: orderVariants.variants,
        mapRow: (row) => row === null || row === void 0 ? void 0 : row.quantities,
        mapCell: (variant) => ({
            id: variant === null || variant === void 0 ? void 0 : variant.id,
            value: variant === null || variant === void 0 ? void 0 : variant.quantity,
        }),
        h: undefined,
        fill: undefined,
        data: undefined,
        rows: undefined,
    }), [orderVariants]);
    const handleQuantitiesChange = useCallback((values) => {
        const variantsQuantity = values.map((e) => e.val);
        updateOrderModelVariant({
            variables: { variantsQuantity },
        }).catch(dataValidationErrorHandler());
    }, [updateOrderModelVariant]);
    const [update] = useDebounceMatrix(handleQuantitiesChange, 1500);
    const quantityChange = useCallback((id, value) => {
        update([id, value], id);
    }, [update]);
    const copySpecificationVolume = useCallback(() => {
        copyOrderModelVariantsFromModel({ variables: { orderModelId: orderModel.id } });
    }, [orderModel, copyOrderModelVariantsFromModel]);
    const modelSummary = useMemo(() => Object.fromEntries(modelVariants.sizes
        .filter((size) => size.isActive)
        .map((size, i) => [
        size.sizeName.name,
        Object.fromEntries((modelMatrix === null || modelMatrix === void 0 ? void 0 : modelMatrix.data.map((row, j) => [
            modelMatrix === null || modelMatrix === void 0 ? void 0 : modelMatrix.object[j].name,
            row[i].value,
        ])) || []),
    ]) || []), [modelVariants, modelMatrix]);
    const orderSummary = useMemo(() => Object.fromEntries(orderVariants.sizes.map((size, i) => [
        size.name,
        Object.fromEntries((orderMatrix === null || orderMatrix === void 0 ? void 0 : orderMatrix.data.map((row, j) => [
            orderMatrix === null || orderMatrix === void 0 ? void 0 : orderMatrix.object[j].name,
            row[i].value,
        ])) || []),
    ]) || []), [orderVariants, orderMatrix]);
    const volumeMatch = useMemo(() => isEqual(modelSummary, orderSummary), [modelSummary, orderSummary]);
    const [ready, setReady] = useState(true); // domyślnie ready żeby zablokować edycję
    useEffect(() => {
        var _a, _b;
        setReady((_b = (_a = orderVolumeData === null || orderVolumeData === void 0 ? void 0 : orderVolumeData.node) === null || _a === void 0 ? void 0 : _a.modelReady) !== null && _b !== void 0 ? _b : true);
    }, [setReady, orderVolumeData]);
    const isDraft = order.status === 'DRAFT';
    const editDisabled = !isDraft || ready;
    const handleReady = useCallback(() => confirmationDialog(t('orders:ready_confirmation_message'), () => () => setOrderModelToReady({
        variables: {
            orderModelId: orderModel.id,
        },
    }), 'orders:ready_confirmation_title', () => () => setReady(false)), [orderModel.id, setOrderModelToReady, t]);
    const confirmSetReady = useCallback(() => {
        setOrderModelToReady({
            variables: {
                orderModelId: orderModel.id,
            },
        });
    }, [setOrderModelToReady, orderModel.id]);
    const doSetNotReady = useCallback(() => {
        setOrderModelToNotReady({
            variables: {
                orderModelId: orderModel.id,
            },
        });
    }, [setOrderModelToNotReady, orderModel.id]);
    const toggleReady = useCallback(() => {
        setReady(!ready);
        if (ready)
            doSetNotReady();
        else if (volumeMatch)
            confirmSetReady();
        else
            handleReady();
    }, [ready, doSetNotReady, volumeMatch, confirmSetReady, handleReady]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "ModelVolume" },
            React.createElement("h4", { className: "mt-0" }, t('order_volume_title')),
            React.createElement(SmartTable, { head: [
                    t('common:version'),
                    ...orderVariants.sizes.map((size) => (size === null || size === void 0 ? void 0 : size.name) || ''),
                ], matrix: orderMatrix === null || orderMatrix === void 0 ? void 0 : orderMatrix.data, renderBeginColumns: (_row, index) => {
                    const variant = orderMatrix === null || orderMatrix === void 0 ? void 0 : orderMatrix.object[index];
                    return [React.createElement("span", null, variant.name || '')];
                }, renderCell: (value) => (React.createElement(QuantityCell, { cellValue: value, onChange: quantityChange, disabled: editDisabled, precision: 0, setRowBusy: setRowBusy })) }),
            React.createElement("div", { className: "flex justify-end mt-4" },
                React.createElement(ThemedButton, { icon: React.createElement(UpIcon, null), onClick: copySpecificationVolume, disabled: editDisabled || rowBusy.length > 0 }, t('copy_specification_volume'))),
            React.createElement("h4", null, t('model_volume_title')),
            React.createElement(SmartTable, { head: [
                    t('common:version'),
                    ...modelVariants.sizes
                        .filter((size) => size.isActive)
                        .map((size) => { var _a; return ((_a = size === null || size === void 0 ? void 0 : size.sizeName) === null || _a === void 0 ? void 0 : _a.name) || ''; }),
                ], matrix: modelMatrix === null || modelMatrix === void 0 ? void 0 : modelMatrix.data, renderBeginColumns: (_row, index) => {
                    const variant = modelMatrix === null || modelMatrix === void 0 ? void 0 : modelMatrix.object[index];
                    return [React.createElement("span", null, variant.name || '')];
                }, renderCell: (value) => React.createElement("span", null, value.value) })),
        isDraft && (React.createElement("div", { className: "OrderModelReady" },
            React.createElement("div", null,
                React.createElement("span", { className: "ready" }, t('order_model_ready')),
                React.createElement(ThemedSwitch, { onChange: toggleReady, checked: ready, disabled: !isDraft || rowBusy.length > 0 }))))));
};
export default ModelVolume;
